import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  FileText,
  Paintbrush,
  RectangleHorizontal,
  RectangleVertical,
  Settings,
  TriangleAlert,
  X,
} from "lucide-react";
import { ChatInput } from "../chat/ChatInput";
import type { DirectorSkillSummary } from "../../api/directorSkills";
import { StyleLibraryModal } from "../canvas-v2/nodes/shared/StyleLibraryModal";
import type { StyleSummary } from "../../api/styles";
import { createNadDebug } from "../../utils/debugLog";
import { cn } from "../../lib/utils";
import { UploadFlyout, type UploadMediaType } from "../shared/UploadFlyout";
import type { ClassifiedMediaFile } from "../../utils/uploadFileValidation";
import {
  canSubmitComposerTurn,
  type DocumentAttachment,
} from "../shared/documentPrompt";
import { LandingReferencePill } from "../landing/LandingReferencePill";
import {
  MAX_LANDING_IMAGE_UPLOADS,
  type LandingGenerateOptions,
  type LandingUploadReference,
  type LandingUploadType,
} from "../landing/landingUploads";
import {
  type VisualStyle,
  type ProductionTarget,
  type AspectRatio,
  ASPECT_RATIOS,
} from "../landing/hero-content";
import { useStyleLabel } from "../../hooks/useStyleLabel";
import { useSignedLandingAssets } from "../../hooks/useSignedLandingAssets";
import { useIsMobileViewport } from "../../hooks/useMediaQuery";
import { useAuthOptional } from "../../contexts/authContextRaw";
import {
  trackLandingPageView,
  trackLandingPromptConsoleStarted,
  trackLandingPromptSubmitted,
} from "../../analytics/events";
import {
  LANDING_DEFAULT_STYLE_ID,
  landingPromptSubmittedProps,
  resolveLandingStyleFamily,
  trackLandingPromptOptionAction,
} from "./landingAnalytics";
import RecentProjectsShelf, {
  type RecentProjectsShelfState,
} from "./RecentProjectsShelf";
import { useNuxTooltipEvents } from "../../onboarding/nuxEvents";

const stylePreviewNameLog = createNadDebug("dw:style-creator-preview-name");

/**
 * Shared pill chrome for every control in the hero composer's bottom toolbar
 * (Upload Asset, Skills, Style, Aspect ratio, Project setup). Bordered,
 * translucent, fully rounded, and compact so secondary options leave room
 * for the prompt and primary Create action.
 */
export const HERO_TOOLBAR_PILL_CLASS =
  "flex h-7 min-w-0 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-white/20 bg-white/5 px-2.5 font-[Fustat] text-sm font-medium text-white/70 transition-colors hover:border-white/35 hover:bg-white/10 hover:text-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:cursor-not-allowed disabled:opacity-50 [&>svg]:h-3.5 [&>svg]:w-3.5";
const mobileLoadingLog = createNadDebug("dw:mobile-ui-loading");
const disclaimerLog = createNadDebug("dw:landing-data-disclaimer");
// v1 upfront project-planning modal — NAD trace seam (dev-gated). Feature tag
// mirrors the backend `feature="upfront-project-plan"` so the modal-open →
// create-with-plan chain reads as one timeline.
const projectPlanLog = createNadDebug("dw:upfront-project-plan");

// Per-session dismissal of the data-privacy disclaimer banner. Version-keyed so a
// future copy revision re-surfaces the banner (mirrors MaintenanceBanner). Uses
// sessionStorage (not localStorage) so this safety reminder re-appears next session.
// Writes happen only inside the mounted hero (post-consent) — never in ConsentBackdrop.
const DATA_DISCLAIMER_DISMISS_KEY = "hero-data-disclaimer-dismissed:v1";
const LOCAL_DEV_SHOWREEL_URL = "/tests/fixtures/showreel-sample.mp4";

// Progressive blur along the hero's BOTTOM edge only (the davinci.ai
// video-generator treatment, applied to one edge rather than all four). Both the
// signed-in and signed-out heroes are full-bleed showreels now, so both get it.
//
// Why three stacked layers instead of one blurred strip: a single
// `backdrop-blur` band has a hard seam where it begins — the pixel above it is
// sharp and the pixel below is fully blurred. Stacking passes whose masks end at
// progressively lower stops ramps the effective radius from 0 to full, so the
// transition reads as a gradual defocus. Radii stay modest because
// `backdrop-filter` over a playing video is GPU-expensive.
// Typed explicitly rather than with `as const` — scripts/check-forbidden-ts-syntax.mjs
// rejects every `as` expression, `as const` included.
const HERO_BOTTOM_BLUR_LAYERS: readonly { blur: string; fadeTo: string }[] = [
  { blur: "6px", fadeTo: "100%" },
  { blur: "18px", fadeTo: "55%" },
  { blur: "40px", fadeTo: "25%" },
];

// Width of the signed-in hero's centred content column — heading, composer and
// the recent-projects shelf all share it so they line up as one stack over the
// full-bleed showreel (Rowell's mockup, Workplace post 1608162530941044).
// Exported so the shelf's own tests can assert the shared column.
export const SIGNED_IN_COLUMN_MAX_W = "max-w-[760px]";

function readDataDisclaimerDismissed(): boolean {
  try {
    return sessionStorage.getItem(DATA_DISCLAIMER_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function persistDataDisclaimerDismissed(): void {
  try {
    sessionStorage.setItem(DATA_DISCLAIMER_DISMISS_KEY, "1");
  } catch {
    // sessionStorage can throw in private mode / when full — dismissal is best-effort.
  }
}

interface HeroSectionV2Props {
  onGenerate?: (prompt: string, options?: LandingGenerateOptions) => void;
  isCreating?: boolean;
  videoEnabled?: boolean;
  selectedStyle: VisualStyle;
  onStyleChange: (style: VisualStyle) => void;
  selectedAspectRatio: AspectRatio;
  onAspectRatioChange: (ratio: AspectRatio) => void;
  productionTarget: ProductionTarget;
  // Landing asset uploads (queued client-side, uploaded after project create).
  // `onUploadFile` is undefined when uploads are unavailable (e.g. logged-out) —
  // the control is hidden in that case, matching the v1 landing.
  uploads?: LandingUploadReference[];
  onUploadFile?: (file: File, type: LandingUploadType) => void;
  // Merged "Upload media" picker: one mixed multi-select (see UploadFlyout).
  onUploadMedia?: (accepted: ClassifiedMediaFile[], rejected: File[]) => void;
  onRemoveUpload?: (id: string) => void;
  // Attached documents (RFC-1218). Unlike uploads these are NOT queued files:
  // the text is extracted at attach time (the landing route needs no project),
  // so a refusal is shown before any project is created. `onUploadDocument` is
  // undefined when uploads are unavailable, hiding the entry like the others.
  documents?: readonly DocumentAttachment[];
  documentPending?: string | null;
  documentError?: string | null;
  onUploadDocument?: (file: File) => void;
  onRemoveDocument?: (index: number) => void;
  // v1 upfront project-planning modal (landing only). `planEnabled` is the flag
  // test-seam: the parent reads UPFRONT_PROJECT_PLAN_ENABLED once and passes it
  // down as a boolean so tests set it via prop (no vi.mock churn). When false the
  // toolbar is byte-for-byte current prod (inline Style + Aspect pills, no setup
  // button). When true the "Project setup" button renders AND the inline aspect
  // pill is hidden (orientation moves into the modal — single owner, no
  // duplicate). `onOpenPlan` opens the modal; `isPlanSet` drives the subtle
  // "Plan set" indicator.
  planEnabled?: boolean;
  onOpenPlan?: () => void;
  isPlanSet?: boolean;
  // RFC-1577: Project setup settings that differ from their defaults, for the
  // prompt-submit event. Undefined while the plan flag is off (no surface).
  projectSetupChangedCount?: number;
  // Present only for the authenticated landing experience. Undefined preserves
  // the signed-out hero's existing layout and prevents any project shelf mount.
  recentProjects?: RecentProjectsShelfState;
}

const PROMPT_MAX_LENGTH = 10_000;

// ⚠️ Keep in sync: the pre-consent cookie backdrop
// (apps/console/src/components/consent/ConsentBackdrop.tsx) is a static visual
// copy of this hero (image, headline/subtitle, prompt card). If you change the
// hero layout/copy/branding, update ConsentBackdrop too. It must stay
// import-free of app code (see CLAUDE.md → UI/UX Guidelines → "Cookie consent
// backdrop").
export default function HeroSectionV2({
  onGenerate,
  isCreating,
  videoEnabled = true,
  selectedStyle,
  onStyleChange,
  selectedAspectRatio,
  onAspectRatioChange,
  productionTarget: _productionTarget,
  uploads = [],
  documents = [],
  documentPending = null,
  documentError = null,
  onUploadDocument,
  onRemoveDocument,
  onUploadFile,
  onUploadMedia,
  onRemoveUpload,
  planEnabled = false,
  onOpenPlan,
  isPlanSet = false,
  projectSetupChangedCount,
  recentProjects,
}: HeroSectionV2Props) {
  const { t } = useTranslation("landing");
  const imageUploadCount = uploads.filter((upload) => upload.type === "image").length;
  const imageUploadCapReached = imageUploadCount >= MAX_LANDING_IMAGE_UPLOADS;
  // Once the image cap is reached, drop 'image' from the allowed set so the
  // image option/input hide while video/audio stay usable. The picker still
  // slices any over-cap selection as an authoritative backstop.
  const uploadAllowedTypes: readonly UploadMediaType[] = imageUploadCapReached
    ? ["video", "audio"]
    : ["image", "video", "audio"];
  const [prompt, setPrompt] = useState("");
  const [ghostText, setGhostText] = useState("");
  // RFC-0974: acquisition-funnel analytics (best-effort, consent-gated). Optional
  // auth read so this stays safe if the hero renders outside an AuthProvider.
  const auth = useAuthOptional();
  const isAuthenticated = !!auth?.isAuthenticated;
  const isSignedInLanding = Boolean(recentProjects);
  const showHeroVideo = videoEnabled;
  const promptStartedRef = useRef(false);
  const [isStyleLibraryOpen, setIsStyleLibraryOpen] = useState(false);
  // Read-only cache access for the style family on option events (no fetch).
  const queryClient = useQueryClient();
  const { emitNuxEvent } = useNuxTooltipEvents();
  const isMobileViewport = useIsMobileViewport();
  const { urls: signedAssets, optimizedUrls } = useSignedLandingAssets({
    enabled: showHeroVideo,
  });
  const signedShowreelUrl = isMobileViewport
    ? optimizedUrls.Showreel
    : signedAssets.Showreel;
  const showreelUrl = showHeroVideo
    ? signedShowreelUrl ?? (import.meta.env.DEV ? LOCAL_DEV_SHOWREEL_URL : undefined)
    : undefined;
  const [selectedStyleMeta, setSelectedStyleMeta] = useState<Pick<
    StyleSummary,
    "id" | "name"
  > | null>(null);
  const [isRatioMenuOpen, setIsRatioMenuOpen] = useState(false);
  const ratioMenuRef = useRef<HTMLDivElement>(null);
  // Read once in the initializer (client-only Vite app) to avoid a show-then-hide flash.
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(
    readDataDisclaimerDismissed,
  );

  const handleDismissDisclaimer = () => {
    persistDataDisclaimerDismissed();
    setDisclaimerDismissed(true);
    disclaimerLog({ event: "dismiss", feature: "landing-data-disclaimer" });
  };

  useEffect(() => {
    disclaimerLog({
      event: "shown",
      feature: "landing-data-disclaimer",
      visible: !disclaimerDismissed,
    });
  }, [disclaimerDismissed]);

  // RFC-0974: `landing_page_view` — once per view on mount. referrer_host is
  // re-normalized server-side (approved host only, query/fragment stripped).
  useEffect(() => {
    let referrerHost: string | undefined;
    try {
      referrerHost = document.referrer ? new URL(document.referrer).hostname : undefined;
    } catch {
      referrerHost = undefined;
    }
    let utmSource: string | undefined;
    let utmCampaign: string | undefined;
    try {
      const params = new URLSearchParams(window.location.search);
      utmSource = params.get("utm_source") ?? undefined;
      utmCampaign = params.get("utm_campaign") ?? undefined;
    } catch {
      /* ignore */
    }
    trackLandingPageView({
      referrer_host: referrerHost,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ghost text typing animation — ported from v1 HeroSection
  useEffect(() => {
    if (prompt.length > 0) {
      setGhostText("");
      return;
    }

    const ghostIdeas = t("hero.ghostTextIdeas.video", { returnObjects: true });
    const ideas: string[] = Array.isArray(ghostIdeas)
      ? ghostIdeas.filter((idea): idea is string => typeof idea === "string")
      : [];
    // Defensive: if the locale value is missing/malformed, don't run the typing
    // loop (it indexes ideas[ideaIndex].substring — would throw on []).
    if (ideas.length === 0) {
      setGhostText("");
      return;
    }
    let ideaIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let isPaused = false;

    const type = () => {
      if (prompt.length > 0) {
        setGhostText("");
        return;
      }

      const currentIdea = ideas[ideaIndex];

      if (isPaused) return;

      if (!isDeleting) {
        setGhostText(currentIdea.substring(0, charIndex + 1));
        charIndex++;

        if (charIndex === currentIdea.length) {
          isPaused = true;
          setTimeout(() => {
            isPaused = false;
            isDeleting = true;
          }, 2000);
        }
      } else {
        setGhostText(currentIdea.substring(0, charIndex - 1));
        charIndex--;

        if (charIndex === 0) {
          isDeleting = false;
          ideaIndex = (ideaIndex + 1) % ideas.length;
        }
      }
    };

    const interval = setInterval(type, isDeleting ? 30 : 50);
    return () => clearInterval(interval);
  }, [prompt, t]);

  // Close ratio menu on outside click / Escape
  useEffect(() => {
    if (!isRatioMenuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && !ratioMenuRef.current?.contains(target)) {
        setIsRatioMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsRatioMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isRatioMenuOpen]);

  // Single source for "may this turn be submitted": a document with no typed
  // instruction is a legitimate turn, and an in-flight extraction blocks both.
  const canCreate = canSubmitComposerTurn({
    trimmedPrompt: prompt.trim(),
    documentCount: documents.length,
    extractionPending: Boolean(documentPending),
  });

  const handleSubmit = (message: string, selectedSkill?: DirectorSkillSummary, selectedSkillPosition?: number): 'sent' | false => {
    const trimmed = message.trim();
    if (selectedSkill && selectedSkillPosition === undefined) {
      throw new Error('Selected landing skill is missing its inline position');
    }
    if (onGenerate && trimmed.length <= PROMPT_MAX_LENGTH && canSubmitComposerTurn({
      trimmedPrompt: trimmed,
      documentCount: documents.length,
      extractionPending: Boolean(documentPending),
    })) {
      // RFC-0974: `landing_page_prompt_submitted` (pre-auth funnel; prompt text
      // is NEVER sent — only its length).
      // RFC-1577 enrichment: style / assets / project setup from the final
      // committed state — ids, booleans, counts and buckets only.
      trackLandingPromptSubmitted(
        landingPromptSubmittedProps({
          promptLength: trimmed.length,
          isAuthenticated,
          styleId: selectedStyle,
          uploads,
          projectSetupChangedCount,
        }),
      );
      onGenerate(trimmed, {
        uploads,
        documents,
        selectedSkill: selectedSkill && selectedSkillPosition !== undefined ? {
          ref: selectedSkill.ref,
          versionId: selectedSkill.versionId,
          name: selectedSkill.name,
          publicSlug: selectedSkill.publicSlug,
          displayName: selectedSkill.displayName,
          position: selectedSkillPosition,
        } : undefined,
      });
      return 'sent';
    }
    return false;
  };

  // RFC-1577 `landing_prompt_option_action` (style): the library opening,
  // and a COMMITTED pick that differs from the current style. Picking the
  // landing default (No style) from another style is a reset.
  const handleOpenStyleLibrary = () => {
    emitNuxEvent({ type: 'controlActivated', controlId: 'styleOpened', anchorId: 'landing-setup-choices' });
    setIsStyleLibraryOpen(true);
    trackLandingPromptOptionAction({
      option_type: "style",
      action: "open",
      style_id: selectedStyle,
      style_family: resolveLandingStyleFamily(queryClient, selectedStyle),
      was_default_before: selectedStyle === LANDING_DEFAULT_STYLE_ID,
      is_authenticated: isAuthenticated,
    });
  };

  const trackStyleCommitted = (styleId: string, style?: StyleSummary) => {
    if (styleId === selectedStyle) return;
    trackLandingPromptOptionAction({
      option_type: "style",
      action: styleId === LANDING_DEFAULT_STYLE_ID ? "reset" : "change",
      style_id: styleId,
      style_family: resolveLandingStyleFamily(queryClient, styleId, style),
      was_default_before: selectedStyle === LANDING_DEFAULT_STYLE_ID,
      is_authenticated: isAuthenticated,
    });
  };

  const handleOpenPlan = () => {
    emitNuxEvent({ type: 'controlActivated', controlId: 'projectSetupOpened', anchorId: 'landing-setup-choices' });
    projectPlanLog({
      event: "plan-modal-open",
      feature: "upfront-project-plan",
      is_plan_set: isPlanSet,
    });
    onOpenPlan?.();
  };

  const selectedMetaLabel =
    selectedStyleMeta?.id === selectedStyle
      ? selectedStyleMeta.name
      : undefined;
  const { label: styleLabel, count: styleCount, source: styleLabelSource } =
    useStyleLabel(selectedStyle, selectedMetaLabel);
  const AspectRatioIcon =
    selectedAspectRatio === "9:16" ? RectangleVertical : RectangleHorizontal;

  useEffect(() => {
    stylePreviewNameLog({
      event: "landing-v2-style-label-resolved",
      feature: "style-creator-preview-name",
      styleId: selectedStyle,
      label: styleLabel,
      source: styleLabelSource,
    });
  }, [selectedStyle, styleLabel, styleLabelSource]);

  useEffect(() => {
    mobileLoadingLog({
      event: "hero-video-eligibility",
      feature: "mobile-ui-loading",
      mobile_viewport: isMobileViewport,
      video_enabled: showHeroVideo,
      signed_url_ready: Boolean(signedShowreelUrl),
      local_fallback: Boolean(showreelUrl && !signedShowreelUrl),
      // Placeholder/poster is now the showreel's own first frame (IP-free) so it
      // matches the video and hands off seamlessly; also the login backdrop still.
      placeholder_first_frame: true,
    });
  }, [isMobileViewport, showHeroVideo, showreelUrl, signedShowreelUrl]);

  return (
    <>
      <section
        className={
          isSignedInLanding
            ? "relative overflow-hidden bg-black"
            : "relative"
        }
      >
        {isSignedInLanding && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "radial-gradient(circle, var(--border-subtle) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          />
        )}
        {/* Hero background — looping showreel video from landing-assets bucket */}
        <div
          data-testid="hero-media"
          className={
            // Signed in the hero is FULL-BLEED (Rowell's mockup on Workplace post
            // 1608162530941044): no inset max-width, no corner radius, so the
            // showreel runs edge to edge under the floating navbar. The signed-out
            // marketing hero keeps its inset rounded panel.
            // `justify-end`, not `justify-center`: in the mockup the heading /
            // composer / shelf stack sits in the LOWER portion of the showreel,
            // leaving the character art visible above it. Centring buried the
            // subject behind the composer.
            isSignedInLanding
              ? "relative flex min-h-[680px] w-full flex-col justify-end overflow-hidden px-4 pb-5 pt-24 sm:px-6 md:min-h-[851px] md:px-[84px] md:pb-7 md:pt-28"
              : "relative min-h-[680px] w-full overflow-hidden md:h-[851px]"
          }
        >
          {/* Placeholder = the showreel's own first frame (IP-free), so it
              matches the video and hands off seamlessly instead of showing an
              unrelated still. `?v=2` busts stale cached bytes for the new bundle.
              Also serves as the login-modal backdrop still (heroVideoEnabled=false). */}
          {(!isSignedInLanding || showHeroVideo) && (
            <img
              src="/landing-v2/hero-bg.jpg?v=2"
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {/* Showreel video — src comes from signed-URL fetch on mount.
              Omitting src until ready avoids a 404 flash on first paint. */}
          {showreelUrl && (
            <video
              data-testid="hero-background-video"
              aria-hidden="true"
              src={showreelUrl}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster="/landing-v2/hero-bg.jpg?v=2"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}

          {/* Bottom-edge progressive blur. Sits ABOVE the showreel and BELOW the
              content, so the heading / composer / shelf stay sharp while the
              video defocuses into the section boundary. See
              HERO_BOTTOM_BLUR_LAYERS for why this is stacked. */}
          <div
            data-testid="hero-bottom-blur"
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-28 md:h-44"
          >
            {HERO_BOTTOM_BLUR_LAYERS.map((layer) => {
              const mask = `linear-gradient(to top, #000 0%, transparent ${layer.fadeTo})`;
              return (
                // Mask and backdrop-filter go on the SAME element, which is the
                // standard progressive-blur technique.
                //
                // They must NOT be split across a masked wrapper + blurred
                // child: per Filter Effects, an ancestor carrying a mask (or
                // filter/opacity) establishes a *Backdrop Root*, and a
                // descendant's backdrop-filter can only sample content painted
                // inside that root — which is empty. Splitting them therefore
                // guarantees no blur at all. On one element the order is
                // backdrop-filter first, then the element's own mask over the
                // composited result, which is what we want.
                <div
                  key={layer.blur}
                  className="absolute inset-0"
                  style={{
                    backdropFilter: `blur(${layer.blur})`,
                    WebkitBackdropFilter: `blur(${layer.blur})`,
                    maskImage: mask,
                    WebkitMaskImage: mask,
                  }}
                />
              );
            })}
          </div>

          {/* Bottom-edge FADE. The blur above defocuses the video's content, but
              the video still *ends* against the next section at a hard
              horizontal line — defocusing pixels cannot soften a boundary. This
              ramps the video into the page background so the edge dissolves
              instead of being a straight cut. Above the blur (z-1), below the
              z-10 content. */}
          <div
            data-testid="hero-bottom-fade"
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-40 bg-gradient-to-b from-transparent via-black/80 to-black md:h-60"
          />

          {/* Contrast overlay for foreground content */}
          {isSignedInLanding ? (
            <div
              data-testid="hero-background-scrim"
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-background/45"
            />
          ) : (
            <div
              className="absolute top-0 left-0 right-0 h-[228px]"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)",
              }}
            />
          )}

          {/* Headline text overlay. Brand identity is owned solely by NavbarV2 →
              BrandMark, which sits directly above this heading — a second logo
              icon here read as redundant visual noise (Workplace post
              1608162530941044), so the signed-in heading stands alone and is
              centred over the composer per Rowell's mockup. */}
          {isSignedInLanding ? (
            <div
              className={`relative z-10 mx-auto w-full ${SIGNED_IN_COLUMN_MAX_W}`}
            >
              <h1
                data-testid="hero-heading"
                className="text-center font-[Fustat] text-hero font-semibold tracking-tight text-foreground"
              >
                {t("recentProjects.heading")}
              </h1>
            </div>
          ) : (
            <div className="absolute left-5 right-5 top-32 z-10 flex flex-col gap-3 md:left-1/2 md:right-auto md:top-[204px] md:w-[1153px] md:-translate-x-1/2 md:flex-row md:items-center md:justify-between">
              <h1
                data-testid="hero-heading"
                className="max-w-sm font-[Fustat] text-hero font-semibold leading-[1.1] text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.3)] md:max-w-[756px] md:text-display-lg md:leading-[1.2]"
              >
                {t('heroV2.heading')}
              </h1>
              <p
                data-testid="hero-subtitle"
                className="max-w-sm font-[Fustat] text-base leading-[1.35] text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.3)] md:max-w-[436px] md:text-xl md:font-medium md:leading-[1.2]"
              >
                {t('heroV2.subtitle')}
              </p>
            </div>
          )}

          {/* Prompt stack — bottom-anchored so the optional disclaimer banner grows
              the stack UPWARD, leaving the prompt card's visual position unchanged.
              Assumes the card's former positioning classes. */}
          <div
            data-testid={isSignedInLanding ? "signed-in-hero-stack" : undefined}
            className={
              isSignedInLanding
                ? `relative z-10 mx-auto mt-8 flex w-full ${SIGNED_IN_COLUMN_MAX_W} flex-col gap-3`
                : "absolute bottom-6 left-4 right-4 z-10 flex flex-col gap-2 md:bottom-[110px] md:left-1/2 md:right-auto md:w-[720px] md:-translate-x-1/2"
            }
          >
            {/* Data-privacy disclaimer banner (dismissible, per-session) */}
            {!disclaimerDismissed && (
              <div
                data-testid="hero-data-disclaimer"
                role="note"
                className="flex items-start gap-3 rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] px-4 py-3 shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px]"
              >
                <TriangleAlert
                  aria-hidden="true"
                  strokeWidth={1.5}
                  className="mt-0.5 h-5 w-5 shrink-0 text-amber-400"
                />
                <p className="flex-1 font-sans text-sm leading-[1.4] text-white/90">
                  {t("heroV2.dataDisclaimer")}
                </p>
                <button
                  type="button"
                  data-testid="hero-data-disclaimer-dismiss"
                  aria-label={t("heroV2.dismissDisclaimer")}
                  onClick={handleDismissDisclaimer}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                >
                  <X aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Queued upload references — bottom-anchored stack grows upward, so
                these sit above the prompt card without shifting it. */}
            {uploads.length > 0 && (
              <div
                data-testid="landing-v2-references"
                className="flex min-h-12 items-center gap-2 overflow-x-auto rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] px-4 py-2 shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px]"
              >
                {uploads.map((upload) => (
                  <LandingReferencePill
                    key={upload.id}
                    upload={upload}
                    onRemove={onRemoveUpload}
                  />
                ))}
                {imageUploadCount > 0 && (
                  <span
                    data-testid="landing-v2-image-count"
                    className="ml-auto shrink-0 self-center whitespace-nowrap pl-2 font-sans text-xs font-medium text-white/50"
                  >
                    {t("hero.imageUploadCount", {
                      count: imageUploadCount,
                      max: MAX_LANDING_IMAGE_UPLOADS,
                    })}
                  </span>
                )}
              </div>
            )}

            {/* Attached documents (RFC-1218). A separate row from the upload
                pills because a document is prompt context, not a visual
                reference — it never becomes an asset or a canvas node. The
                error line lives here too so a refusal appears next to the thing
                that was refused, before the user reaches Create. */}
            {(documents.length > 0 || documentPending || documentError) && (
              <div
                data-testid="landing-v2-documents"
                className="flex flex-col gap-1 rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] px-4 py-2 shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px]"
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  {documents.map((doc, index) => (
                    <span
                      key={`${doc.filename}-${index}`}
                      data-testid="landing-v2-document-chip"
                      className="inline-flex items-center gap-1.5 rounded-md border border-white/25 bg-white/10 px-2 py-1 text-xs font-medium text-white/80"
                    >
                      <FileText className="h-3.5 w-3.5 text-white/60" strokeWidth={1.5} />
                      <span className="max-w-[180px] truncate" title={doc.filename}>
                        {doc.filename}
                      </span>
                      <span className="text-white/50">
                        {t("hero.documentChars", { count: doc.charCount })}
                      </span>
                      {/* Screening that passes is invisible, which makes the
                          feature feel unguarded. This is the only signal the
                          user gets that embedded images were checked. */}
                      {doc.clearedImageCount > 0 && (
                        <span className="text-white/50" data-testid="landing-v2-document-images">
                          {t("hero.documentImagesChecked", { count: doc.clearedImageCount })}
                        </span>
                      )}
                      {onRemoveDocument && (
                        <button
                          type="button"
                          onClick={() => onRemoveDocument(index)}
                          className="rounded text-white/50 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                          aria-label={t("hero.removeDocument", { filename: doc.filename })}
                          data-testid="landing-v2-document-remove"
                        >
                          <X className="h-3 w-3" strokeWidth={2} />
                        </button>
                      )}
                    </span>
                  ))}
                  {documentPending && (
                    <span
                      data-testid="landing-v2-document-pending"
                      className="inline-flex items-center gap-1.5 rounded-md border border-white/25 bg-white/10 px-2 py-1 text-xs font-medium text-white/60"
                    >
                      {t("hero.documentExtracting", { filename: documentPending })}
                    </span>
                  )}
                </div>
                {documentError && (
                  <p
                    data-testid="landing-v2-document-error"
                    className="text-xs font-medium text-red-300"
                  >
                    {documentError}
                  </p>
                )}
              </div>
            )}

            {/* Prompt input card */}
            <div
              data-testid="hero-composer-card"
              className="flex h-[300px] flex-col rounded-[20px] border-[1.5px] border-white/50 bg-[rgba(42,42,42,0.7)] p-4 shadow-[0_4px_4px_rgba(0,0,0,0.25)] backdrop-blur-[20px] md:relative md:block md:h-[170px] md:w-full md:p-0"
            >
            <ChatInput
              disabled={Boolean(isCreating || documentPending)}
              placeholder={ghostText}
              onSend={(message, _attachments, _references, skill, position) =>
                handleSubmit(message, skill, position)
              }
              embedded={{
                skillTriggerClassName: HERO_TOOLBAR_PILL_CLASS,
                skillTriggerIconClassName: "h-4 w-4 text-white/85",
                editorClassName: `hero-prompt-editor relative h-32 w-full overflow-y-auto whitespace-pre-wrap break-words bg-transparent font-sans text-base leading-[1.6] text-white outline-0 md:absolute md:left-[18.5px] md:top-[14.5px] md:h-[70px] md:w-auto md:right-[18.5px]`,
                allowEmptySend: documents.length > 0,
                onTextChange: (next) => {
                  if (!promptStartedRef.current && next.trim().length > 0) {
                    promptStartedRef.current = true;
                    trackLandingPromptConsoleStarted({
                      prompt_len: next.length,
                      is_authenticated: isAuthenticated,
                    });
                  }
                  setPrompt(next);
                },
                renderToolbar: (skillPicker, submit) => <>
            {/* Character count */}
            <div className="mt-2 self-end font-mono text-xs font-medium text-white/25 md:absolute md:right-[18.5px] md:top-[90px] md:mt-0">
              <span className={prompt.length > PROMPT_MAX_LENGTH ? "text-destructive" : undefined}>{prompt.length} / {PROMPT_MAX_LENGTH}</span>
            </div>

            {/* Bottom toolbar */}
            <div className="mt-auto flex flex-col gap-3 md:absolute md:bottom-[20px] md:left-[18.5px] md:right-[18.5px] md:mt-0 md:h-[38px] md:flex-row md:items-center md:justify-between">
              {/* Left: controls */}
              <div className="flex min-w-0 flex-wrap items-center gap-1.5 md:flex-nowrap">
                {/* Upload Asset — hidden when uploads unavailable (e.g. logged-out),
                    matching the v1 landing composer. */}
                {onUploadFile && (
                  <UploadFlyout
                    onUploadFile={onUploadFile}
                    onUploadMedia={onUploadMedia}
                    onUploadDocument={onUploadDocument}
                    allowedTypes={uploadAllowedTypes}
                    disabled={isCreating}
                    size="sm"
                    testIdPrefix="landing-v2-upload"
                    title={t("hero.addReference")}
                    label={t("hero.uploadAsset")}
                    buttonClassName={HERO_TOOLBAR_PILL_CLASS}
                  />
                )}

                <div
                  className="flex min-w-0 flex-wrap items-center gap-1.5 md:flex-nowrap"
                  data-nux-anchor="landing-setup-choices"
                  onClickCapture={(event) => {
                    const target = event.target
                    if (!(target instanceof HTMLElement)) return
                    if (target.closest('[data-testid="director-skill-picker-trigger"]')) {
                      emitNuxEvent({ type: 'controlActivated', controlId: 'skillsOpened', anchorId: 'landing-setup-choices' });
                    }
                  }}
                >
                {skillPicker}

                {/* Style picker */}
                <button
                  data-testid="style-library-button"
                  type="button"
                  onClick={handleOpenStyleLibrary}
                  // The style label is user-controlled length: this is the one
                  // pill allowed to shrink (its label truncates) so the row
                  // never overflows the card.
                  className={cn(HERO_TOOLBAR_PILL_CLASS, "shrink")}
                  title={t('heroV2.styleCount', { label: styleLabel, count: styleCount })}
                >
                  <Paintbrush className="h-4 w-4 shrink-0 text-white/85" />
                  <span className="min-w-0 max-w-[172px] truncate md:max-w-none">
                    {t('heroV2.styleCount', { label: styleLabel, count: styleCount })}
                  </span>
                </button>

                {/* Aspect ratio — inline quick control ONLY while the plan flag
                    is off (byte-for-byte the current prod toolbar). When the flag
                    is on, orientation MOVES into the "Project setup" modal (single
                    owner: LandingPageV2.currentAspectRatio) so the toolbar stays
                    two clean entries: Style + Project setup. */}
                {!planEnabled && (
                  <div className="relative" ref={ratioMenuRef}>
                    <button
                      data-testid="aspect-ratio-button"
                      type="button"
                      aria-haspopup="menu"
                      aria-expanded={isRatioMenuOpen}
                      onClick={() => setIsRatioMenuOpen((open) => !open)}
                      className={HERO_TOOLBAR_PILL_CLASS}
                    >
                      <AspectRatioIcon
                        data-testid={`aspect-ratio-icon-${selectedAspectRatio.replace(":", "-")}`}
                        className="h-4 w-4 text-white/85"
                        strokeWidth={1.5}
                      />
                      <span>
                        {selectedAspectRatio}
                      </span>
                    </button>

                    {isRatioMenuOpen && (
                      <div
                        data-testid="aspect-ratio-menu"
                        role="menu"
                        className="absolute left-0 bottom-full mb-2 z-20 w-28 rounded-lg border border-white/20 bg-[rgba(30,30,30,0.95)] backdrop-blur-xl shadow-xl p-1"
                      >
                        {ASPECT_RATIOS.map((ratio) => (
                          <button
                            key={ratio}
                            type="button"
                            role="menuitemradio"
                            aria-checked={selectedAspectRatio === ratio}
                            onClick={() => {
                              onAspectRatioChange(ratio);
                              setIsRatioMenuOpen(false);
                            }}
                            className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors cursor-pointer ${
                              selectedAspectRatio === ratio
                                ? "bg-white/15 text-white"
                                : "text-white/60 hover:bg-white/10 hover:text-white"
                            }`}
                          >
                            <span>{ratio}</span>
                            {selectedAspectRatio === ratio && (
                              <svg
                                className="w-3.5 h-3.5 text-white"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Project setup — opt-in upfront planner (relabeled from
                    "Settings"). Flag-gated (ship dark). Opens the "Your project
                    plan" modal, which owns orientation + video defaults + story.
                    The /login backdrop is already inert, so no per-button
                    inert/tabindex guard is needed (GAP-1a). */}
                {planEnabled && (
                  <button
                    type="button"
                    data-testid="project-plan-button"
                    aria-label={t("projectPlanV2.openButtonAria")}
                    // The explicit aria-label above becomes the button's accessible
                    // NAME, which would otherwise swallow the nested sr-only "plan
                    // set" text. Expose the state as a DESCRIPTION instead, only when
                    // set (the referenced node renders only then).
                    aria-describedby={isPlanSet ? "project-plan-set-desc" : undefined}
                    onClick={handleOpenPlan}
                    className={HERO_TOOLBAR_PILL_CLASS}
                  >
                    <Settings
                      className="h-4 w-4 shrink-0 text-white/85"
                      aria-hidden="true"
                    />
                    <span>
                      {t("projectPlanV2.openButton")}
                    </span>
                    {/* Subtle "plan set" affordance — a small dot after the label
                        instead of a filled text chip (design polish: keeps the
                        button uncluttered). Orientation lives/edits in the modal,
                        so it is intentionally NOT summarized here. Same testid is
                        preserved; the dot is decorative (aria-hidden) with an
                        sr-only label linked to the button via aria-describedby. */}
                    {isPlanSet && (
                      <span
                        data-testid="project-plan-set-indicator"
                        title={t("projectPlanV2.planSetIndicator")}
                        className="ml-1 inline-flex items-center"
                      >
                        <span
                          aria-hidden="true"
                          className="h-1.5 w-1.5 rounded-full bg-white/80"
                        />
                        <span id="project-plan-set-desc" className="sr-only">
                          {t("projectPlanV2.planSetIndicator")}
                        </span>
                      </span>
                    )}
                  </button>
                )}
                </div>
              </div>

              {/* Create button */}
              <button
                data-testid="create-btn"
                onClick={submit}
                disabled={isCreating || !canCreate || prompt.length > PROMPT_MAX_LENGTH}
                className="flex h-9 w-full shrink-0 items-center justify-center gap-1.5 rounded-full bg-white px-5 font-sans text-sm font-semibold text-[#0d0d0d] shadow-[0_4px_6px_rgba(0,0,0,0.25)] transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:cursor-not-allowed disabled:opacity-50 md:h-[38px] md:w-auto md:text-base"
              >
                {isCreating ? t('heroV2.creating') : t('heroV2.create')}
                {!isCreating && (
                  <ArrowRight className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
                )}
              </button>
            </div>
                </>,
              }}
            />
            </div>

            {recentProjects && (
              <RecentProjectsShelf {...recentProjects} />
            )}
          </div>
        </div>
      </section>

      {/* Style Library Modal — rendered outside the section stacking context */}
      <StyleLibraryModal
        isOpen={isStyleLibraryOpen}
        onClose={() => setIsStyleLibraryOpen(false)}
        currentStyleId={selectedStyle}
        onApply={(id, style) => {
          trackStyleCommitted(id, style);
          onStyleChange(id);
          setSelectedStyleMeta(
            style ? { id: style.id, name: style.name } : null,
          );
          setIsStyleLibraryOpen(false);
        }}
      />
    </>
  );
}
